import { api } from './api';

/**
 * Lädt ein Schülerfoto aus dem Dateisystem
 * @param sophomorixUnid Die ID des Schülers
 * @returns Promise<string | null> Base64-String des Fotos oder null wenn nicht gefunden
 */
export async function loadStudentPhoto(sophomorixUnid: string): Promise<string | null> {
  try {
    const response = await api.get(`/student-photos/${sophomorixUnid}`);
    
    if (response.data.success && response.data.photo) {
      return response.data.photo;
    }
    
    return null;
  } catch (error) {
    console.warn(`Foto für Schüler ${sophomorixUnid} nicht gefunden:`, error);
    return null;
  }
}

/**
 * Lädt mehrere Schülerfotos auf einmal (für Listen)
 * @param sophomorixUnids Array der Schüler-IDs
 * @returns Promise<Record<string, string | null>> Objekt mit sophomorixUnid als Key und Foto als Value
 */
export async function loadStudentPhotos(sophomorixUnids: string[]): Promise<Record<string, string | null>> {
  try {
    if (sophomorixUnids.length === 0) {
      return {};
    }
    
    const ids = sophomorixUnids.join(',');
    const response = await api.get(`/student-photos/batch?ids=${ids}`);
    
    if (response.data.success && response.data.photos) {
      return response.data.photos;
    }
    
    // Fallback: leeres Objekt mit null-Werten
    const emptyPhotos: Record<string, string | null> = {};
    sophomorixUnids.forEach(id => {
      emptyPhotos[id] = null;
    });
    return emptyPhotos;
  } catch (error) {
    console.warn('Fehler beim Laden der Schülerfotos:', error);
    
    // Fallback: leeres Objekt mit null-Werten
    const emptyPhotos: Record<string, string | null> = {};
    sophomorixUnids.forEach(id => {
      emptyPhotos[id] = null;
    });
    return emptyPhotos;
  }
}

/**
 * Erstellt eine Data URL für ein Base64-Foto
 * @param photoBase64 Base64-String des Fotos
 * @returns string Data URL für das Foto
 */
export function createPhotoDataUrl(photoBase64: string): string {
  // Prüfe ob es bereits eine Data URL ist
  if (photoBase64.startsWith('data:')) {
    return photoBase64;
  }
  
  // Bestimme den korrekten MIME-Type basierend auf dem Base64-Header
  let mimeType = 'image/jpeg'; // Standard
  
  if (photoBase64.startsWith('iVBORw0KGgo')) {
    mimeType = 'image/png';
  } else if (photoBase64.startsWith('/9j/')) {
    mimeType = 'image/jpeg';
  } else if (photoBase64.startsWith('R0lGOD')) {
    mimeType = 'image/gif';
  } else if (photoBase64.startsWith('UklGR')) {
    mimeType = 'image/webp';
  }
  
  return `data:${mimeType};base64,${photoBase64}`;
}

/**
 * Prüft ob ein Foto verfügbar ist
 * @param photo Base64-String oder null
 * @returns boolean True wenn Foto verfügbar ist
 */
export function hasPhoto(photo: string | null | undefined): boolean {
  return !!(photo && photo.trim().length > 0);
}

/**
 * Erstellt eine Foto-URL für die direkte Anzeige (ohne Base64)
 * @param sophomorixUnid Die ID des Schülers
 * @returns string URL für das Foto
 */
export function getPhotoUrl(sophomorixUnid: string): string {
  return `/api/student-photos/${sophomorixUnid}/raw`;
}
